import { useCallback, useRef, useState } from 'react'
import { UndoAtomicAction } from '@/lib/history/types/undo-atomic-action'
import type { UndoAction } from '@/lib/history/types/undo-action'

export interface UndoRedoState {
  canUndo: boolean
  canRedo: boolean
  pushAction: (action: UndoAction) => void
  /**
   * Takes the topmost step to undo that doesn't match `shouldSkip` and moves it to the redo stack.
   * Steps that match stay in history untouched, so one call skips straight past steps that can't be
   * replayed from here (another spread's) without losing them. Null, with nothing moved, when every
   * step is skipped.
   */
  undo: (shouldSkip?: (action: UndoAction) => boolean) => UndoAction | null
  /** `undo`'s mirror for the redo stack, `shouldSkip` included. */
  redo: (shouldSkip?: (action: UndoAction) => boolean) => UndoAction | null
  clear: () => void
  /** Current undo stack depth — lets a caller (e.g. crop mode) snapshot "how far back is my own
   *  session's history" and later refuse to undo past that point, without needing to inspect the
   *  stack's contents. Reads live off the ref, no re-render dependency. */
  getUndoStackSize: () => number
  /**
   * Pops the top of the undo stack only if it satisfies `predicate`, without touching the redo
   * stack — used to silently erase an action (e.g. a no-op preset-to-path conversion) rather than
   * undo it, so it can never be redone back in. Returns the popped action, or null if the top
   * didn't match (or the stack is empty), leaving the stack untouched in that case.
   */
  popIfMatches: (predicate: (action: UndoAction) => boolean) => UndoAction | null
  /**
   * Reverses the stack move of an undo/redo step whose replay didn't take effect (e.g. its save
   * failed and rolled back): after an `'undo'` the step goes from the top of the redo stack back
   * onto the undo stack, after a `'redo'` the other way. Only when that top entry satisfies
   * `predicate`; neither stack is otherwise touched. Returns the moved action, or null.
   */
  /**
   * Like `revertHistoryStepIfMatches`, but drops the step instead of putting it back — for a replay
   * that failed for good (retrying would fail the same way and block every older step).
   */
  dropReplayedStepIfMatches: (
    direction: 'undo' | 'redo',
    predicate: (action: UndoAction) => boolean
  ) => UndoAction | null
  /** Recomputes `canUndo` / `canRedo` — call when the `shouldSkip` option's answer may have
   *  changed without the stacks changing (e.g. on a spread switch). */
  refreshAvailability: () => void
  revertHistoryStepIfMatches: (
    direction: 'undo' | 'redo',
    predicate: (action: UndoAction) => boolean
  ) => UndoAction | null
}

const MAX_HISTORY = 100

function selectionIdSetsEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  const sa = [...a].sort()
  const sb = [...b].sort()

  return sa.every((id, i) => id === sb[i])
}

function optionalSelectionIdSetsEqual(a?: string[], b?: string[]): boolean {
  const left = a ?? []
  const right = b ?? []

  return selectionIdSetsEqual(left, right)
}

function hasSelectionTextTransition(before?: string[], after?: string[]): boolean {
  return !selectionIdSetsEqual(before ?? [], after ?? [])
}

function selectionActionCount(
  action: Extract<UndoAtomicAction, { type: 'selection' }>,
  phase: 'before' | 'after'
): number {
  if (phase === 'before') {
    return action.beforeIds.length + (action.beforeTextIds?.length ?? 0)
  }

  return action.afterIds.length + (action.afterTextIds?.length ?? 0)
}

/**
 * Index of the topmost step in `stack` that `shouldSkip` doesn't pass over, or -1. Skipped steps
 * stay where they are: they belong to another spread and count again when the user is back on it.
 */
function topPlayableIndex(
  stack: UndoAction[],
  shouldSkip: ((action: UndoAction) => boolean) | undefined
): number {
  for (let i = stack.length - 1; i >= 0; i--) {
    if (!shouldSkip?.(stack[i])) return i
  }

  return -1
}

function isSelectionAction(
  action: UndoAction
): action is Extract<UndoAtomicAction, { type: 'selection' }> {
  return action.type === 'selection'
}

/**
 * A hook that manages an undo/redo history stack for shape and text box operations.
 *
 * Returns push/undo/redo functions and boolean flags. The consumer is
 * responsible for actually applying or reverting the returned actions
 * to the ShapeManager and/or TextManager.
 */
export type UseUndoRedoOptions = {
  /**
   * Steps that can't be replayed from here (e.g. an edit on another spread). `undo` / `redo` pass
   * over them, and `canUndo` / `canRedo` ignore them — so Undo and Redo only look available when
   * pressing them will actually do something.
   */
  shouldSkip?: (action: UndoAction) => boolean
}

/** True when `stack` holds at least one step that `shouldSkip` won't drop. */
function hasPlayableStep(
  stack: UndoAction[],
  shouldSkip: ((action: UndoAction) => boolean) | undefined
): boolean {
  return shouldSkip ? stack.some((action) => !shouldSkip(action)) : stack.length > 0
}

export function useUndoRedo(options: UseUndoRedoOptions = {}): UndoRedoState {
  const shouldSkipRef = useRef(options.shouldSkip)

  shouldSkipRef.current = options.shouldSkip

  const undoStack = useRef<UndoAction[]>([])
  const redoStack = useRef<UndoAction[]>([])
  // Trigger re-renders when stack emptiness changes
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)

  const syncFlags = useCallback(() => {
    setCanUndo(hasPlayableStep(undoStack.current, shouldSkipRef.current))
    setCanRedo(hasPlayableStep(redoStack.current, shouldSkipRef.current))
  }, [])

  const pushAction = useCallback(
    (action: UndoAction) => {
      if (action.type === 'selection') {
        // Command/system selection transitions without a transaction are usually incidental noise.
        if (!action.historyTxnId && action.selectionSource && action.selectionSource !== 'user') {
          return
        }

        const prev = undoStack.current[undoStack.current.length - 1]

        if (
          prev?.type === 'selection' &&
          selectionIdSetsEqual(prev.beforeIds, action.beforeIds) &&
          selectionIdSetsEqual(prev.afterIds, action.afterIds) &&
          optionalSelectionIdSetsEqual(prev.beforeTextIds, action.beforeTextIds) &&
          optionalSelectionIdSetsEqual(prev.afterTextIds, action.afterTextIds)
        ) {
          // Ignore duplicate adjacent selection transitions from overlapping gesture paths.
          // Keep the earliest snapshot for stable undo replay, but backfill any missing chrome.
          undoStack.current[undoStack.current.length - 1] = {
            ...prev,
            beforeTextIds: prev.beforeTextIds ?? action.beforeTextIds,
            afterTextIds: prev.afterTextIds ?? action.afterTextIds,
            multiSelectChromeBefore: prev.multiSelectChromeBefore ?? action.multiSelectChromeBefore,
            multiSelectChromeAfter: prev.multiSelectChromeAfter ?? action.multiSelectChromeAfter,
          }
          redoStack.current = []
          syncFlags()
          return
        }

        if (
          prev?.type === 'selection' &&
          selectionIdSetsEqual(prev.beforeIds, action.beforeIds) &&
          selectionIdSetsEqual(prev.afterIds, action.afterIds) &&
          (prev.selectionSource ?? 'user') === (action.selectionSource ?? 'user') &&
          (prev.selectionGesture ?? 'replace') === (action.selectionGesture ?? 'replace')
        ) {
          const prevHasTextTransition = hasSelectionTextTransition(
            prev.beforeTextIds,
            prev.afterTextIds
          )
          const actionHasTextTransition = hasSelectionTextTransition(
            action.beforeTextIds,
            action.afterTextIds
          )

          // Overlapping clear/deselect gesture paths can emit the same shape transition twice,
          // where one entry has text-selection metadata and the other does not.
          // Keep only one history entry, preferring the one that includes the text transition.
          if (prevHasTextTransition !== actionHasTextTransition) {
            const preferred = prevHasTextTransition ? prev : action
            const fallback = prevHasTextTransition ? action : prev

            undoStack.current[undoStack.current.length - 1] = {
              ...preferred,
              beforeTextIds: preferred.beforeTextIds ?? fallback.beforeTextIds,
              afterTextIds: preferred.afterTextIds ?? fallback.afterTextIds,
              multiSelectChromeBefore:
                preferred.multiSelectChromeBefore ?? fallback.multiSelectChromeBefore,
              multiSelectChromeAfter:
                preferred.multiSelectChromeAfter ?? fallback.multiSelectChromeAfter,
            }
            redoStack.current = []
            syncFlags()
            return
          }
        }

        if (
          prev &&
          isSelectionAction(prev) &&
          prev.selectionSource === 'user' &&
          action.selectionSource === 'user' &&
          (prev.selectionGesture ?? 'replace') === 'replace' &&
          (action.selectionGesture ?? 'replace') === 'replace' &&
          selectionIdSetsEqual(prev.beforeIds, action.afterIds) &&
          selectionIdSetsEqual(prev.afterIds, action.beforeIds) &&
          optionalSelectionIdSetsEqual(prev.beforeTextIds, action.afterTextIds) &&
          optionalSelectionIdSetsEqual(prev.afterTextIds, action.beforeTextIds)
        ) {
          // Only collapse tiny inverse transitions (typically misclick correction).
          // For larger mixed/multi selections, B->A is often intentional (e.g. clear selection)
          // and must remain undoable.
          const maxSelectionCardinality = Math.max(
            selectionActionCount(prev, 'before'),
            selectionActionCount(prev, 'after'),
            selectionActionCount(action, 'before'),
            selectionActionCount(action, 'after')
          )

          if (maxSelectionCardinality <= 1) {
            undoStack.current.pop()
            redoStack.current = []
            syncFlags()
            return
          }
        }
      }

      undoStack.current.push(action)
      // Trim history to prevent unbounded growth
      if (undoStack.current.length > MAX_HISTORY) {
        undoStack.current = undoStack.current.slice(-MAX_HISTORY)
      }

      // Any new action invalidates the redo stack
      redoStack.current = []
      syncFlags()
    },
    [syncFlags]
  )

  const undo = useCallback(
    (shouldSkip = shouldSkipRef.current): UndoAction | null => {
      const playableIndex = topPlayableIndex(undoStack.current, shouldSkip)
      const action = playableIndex >= 0 ? undoStack.current.splice(playableIndex, 1)[0] : null

      if (action) {
        if (action.type === 'compound') {
          redoStack.current.push(action)
          syncFlags()
          return action
        }

        const txnId = action.historyTxnId

        if (!txnId) {
          redoStack.current.push(action)
          syncFlags()
          return action
        }

        const grouped: UndoAtomicAction[] = [action]

        // The steps of one transaction sit right below the one taken (it was the top, or the top
        // playable step with another spread's steps above it).
        for (let below = playableIndex - 1; below >= 0; below--) {
          const prev = undoStack.current[below]

          if (prev.type === 'compound' || prev.historyTxnId !== txnId) break
          grouped.push(undoStack.current.splice(below, 1)[0] as UndoAtomicAction)
        }

        const groupedAction: UndoAction =
          grouped.length > 1 ? { type: 'compound', actions: grouped, historyTxnId: txnId } : action

        redoStack.current.push(groupedAction)
        syncFlags()
        return groupedAction
      }

      syncFlags()
      return null
    },
    [syncFlags]
  )

  const redo = useCallback(
    (shouldSkip = shouldSkipRef.current): UndoAction | null => {
      const playableIndex = topPlayableIndex(redoStack.current, shouldSkip)
      const action = playableIndex >= 0 ? redoStack.current.splice(playableIndex, 1)[0] : null

      if (action) {
        undoStack.current.push(action)
      }

      syncFlags()
      return action
    },
    [syncFlags]
  )

  const clear = useCallback(() => {
    undoStack.current = []
    redoStack.current = []
    syncFlags()
  }, [syncFlags])

  const getUndoStackSize = useCallback(() => undoStack.current.length, [])

  const popIfMatches = useCallback(
    (predicate: (action: UndoAction) => boolean): UndoAction | null => {
      const top = undoStack.current[undoStack.current.length - 1]

      if (!top || !predicate(top)) return null

      undoStack.current.pop()
      syncFlags()

      return top
    },
    [syncFlags]
  )

  const dropReplayedStepIfMatches = useCallback(
    (direction: 'undo' | 'redo', predicate: (action: UndoAction) => boolean): UndoAction | null => {
      // After an undo the step sits on top of the redo stack; after a redo, on top of undo.
      const stack = direction === 'undo' ? redoStack.current : undoStack.current
      const top = stack[stack.length - 1]

      if (!top || !predicate(top)) return null

      stack.pop()
      syncFlags()

      return top
    },
    [syncFlags]
  )

  const revertHistoryStepIfMatches = useCallback(
    (direction: 'undo' | 'redo', predicate: (action: UndoAction) => boolean): UndoAction | null => {
      const from = direction === 'undo' ? redoStack.current : undoStack.current
      const to = direction === 'undo' ? undoStack.current : redoStack.current
      const top = from[from.length - 1]

      if (!top || !predicate(top)) return null

      from.pop()
      to.push(top)
      syncFlags()

      return top
    },
    [syncFlags]
  )

  return {
    canUndo,
    canRedo,
    pushAction,
    undo,
    redo,
    clear,
    getUndoStackSize,
    popIfMatches,
    revertHistoryStepIfMatches,
    dropReplayedStepIfMatches,
    refreshAvailability: syncFlags,
  }
}
